import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { Calculator, Plus, Trash2, ShieldAlert, Sparkles, RefreshCw } from 'lucide-react';

interface Property {
  id: string;
  address: string;
  lotWidth: number; // in feet
  lotDepth: number; // in feet
  isCorner: boolean;
}

const DEFAULT_PROPERTIES: Property[] = [
  { id: '1', address: '101 Pine Ln', lotWidth: 60, lotDepth: 120, isCorner: false },
  { id: '2', address: '102 Pine Ln', lotWidth: 70, lotDepth: 120, isCorner: false },
  { id: '3', address: '103 Pine Ln', lotWidth: 90, lotDepth: 130, isCorner: true },
  { id: '4', address: '104 Pine Ln', lotWidth: 55, lotDepth: 110, isCorner: false },
  { id: '5', address: '105 Pine Ln', lotWidth: 80, lotDepth: 125, isCorner: false }
];

export function NeighborhoodAssessmentCalculator() {
  const [totalCost, setTotalCost] = useState('10000');
  const [method, setMethod] = useState<'flat' | 'proportional' | 'tiered'>('flat');
  const [properties, setProperties] = useState<Property[]>(DEFAULT_PROPERTIES);

  // New property form
  const [address, setAddress] = useState('');
  const [width, setWidth] = useState('60');
  const [depth, setDepth] = useState('120');
  const [isCorner, setIsCorner] = useState(false);

  // Constants
  const cornerPremiumFactor = 1.25; // Corner lots pay 25% premium in tiered method

  const handleAddProperty = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;

    setProperties(prev => [
      ...prev,
      {
        id: `p_${Date.now()}`,
        address: address.trim(),
        lotWidth: parseFloat(width) || 50,
        lotDepth: parseFloat(depth) || 100,
        isCorner
      }
    ]);

    setAddress('');
    setIsCorner(false);
  };

  const loadSampleData = () => {
    setTotalCost('10000');
    setProperties(DEFAULT_PROPERTIES);
  };

  const clearAll = () => {
    setProperties([]);
  };

  // Calculations
  const calculatedCost = parseFloat(totalCost) || 0;

  const totalLotArea = useMemo(() => {
    return properties.reduce((acc, prop) => acc + (prop.lotWidth * prop.lotDepth), 0);
  }, [properties]);

  const totalTierUnits = useMemo(() => {
    return properties.reduce((acc, prop) => acc + (prop.isCorner ? cornerPremiumFactor : 1), 0);
  }, [properties]);

  // Compute property shares
  const propertyShares = useMemo(() => {
    if (properties.length === 0) return [];

    return properties.map(prop => {
      const area = prop.lotWidth * prop.lotDepth;
      let share = 0;
      let explanation = '';

      if (method === 'flat') {
        share = calculatedCost / properties.length;
        explanation = `Equal split: 1 / ${properties.length}`;
      } else if (method === 'proportional') {
        const percentage = totalLotArea > 0 ? area / totalLotArea : 0;
        share = calculatedCost * percentage;
        explanation = `${((percentage) * 100).toFixed(1)}% of total area (${area.toLocaleString()} sq ft)`;
      } else if (method === 'tiered') {
        const units = prop.isCorner ? cornerPremiumFactor : 1;
        const ratePerUnit = totalTierUnits > 0 ? calculatedCost / totalTierUnits : 0;
        share = ratePerUnit * units;
        explanation = prop.isCorner 
          ? `Corner Premium: 1.25x weight` 
          : `Standard lot: 1.00x weight`;
      }

      return {
        ...prop,
        area,
        share,
        explanation
      };
    });
  }, [properties, method, calculatedCost, totalLotArea, totalTierUnits]);

  // Summary Metrics
  const totalSharesCalculated = useMemo(() => propertyShares.reduce((acc, p) => acc + p.share, 0), [propertyShares]);

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="bg-[#0044FF] border-4 border-black text-white p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white">
        <h3 className="font-display font-black text-2xl uppercase tracking-tight mb-2">Neighborhood Special Assessment Modeler</h3>
        <p className="font-bold text-sm max-w-xl text-blue-100 dark:text-gray-300">
          Determine fair, local voluntary assessment structures for neighborhood road work, utilities, or capital projects.
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={loadSampleData} variant="secondary" className="bg-[#FFD600] text-black hover:bg-white border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={clearAll} variant="ghost" className="border-2 border-white text-white hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Clear All
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Settings Column */}
        <div className="space-y-6 lg:col-span-1">
          
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-lg uppercase tracking-tight flex items-center gap-2">
              <Calculator className="text-[#0044FF]" size={20} /> Parameters
            </h4>
            
            <div className="space-y-4 text-sm">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Total Project Cost ($)</label>
                <Input 
                  type="number" 
                  value={totalCost} 
                  onChange={e => setTotalCost(e.target.value)} 
                  className="border-2 border-black rounded-none h-10 font-mono font-bold" 
                />
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Assessment Split Rule</label>
                <select 
                  value={method} 
                  onChange={e => setMethod(e.target.value as any)}
                  className="w-full border-2 border-black rounded-none h-10 px-2 font-bold bg-white dark:bg-black dark:text-white text-xs"
                >
                  <option value="flat">Flat Assessment (Equal shares)</option>
                  <option value="proportional">Proportional Share (By Lot Area)</option>
                  <option value="tiered">Corner Lot Tier (1.25x premium factor)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Add Sample Home Form */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-lg uppercase tracking-tight">Add Lot Simulation</h4>
            
            <form onSubmit={handleAddProperty} className="space-y-3 text-sm">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Street Address</label>
                <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="106 Pine Ln" className="border-2 border-black rounded-none h-10 font-bold" />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Lot Width (ft)</label>
                  <Input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="60" className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Lot Depth (ft)</label>
                  <Input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="120" className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
              </div>

              <div className="flex items-center gap-2 py-1">
                <input 
                  type="checkbox" 
                  id="cornerLot" 
                  checked={isCorner} 
                  onChange={e => setIsCorner(e.target.checked)} 
                  className="w-4 h-4 text-[#0044FF] border-2 border-black rounded-none focus:ring-0"
                />
                <label htmlFor="cornerLot" className="text-xs font-bold uppercase tracking-wide cursor-pointer select-none">Is Corner Lot</label>
              </div>

              <Button type="submit" className="w-full bg-[#FFD600] text-black border-2 border-black rounded-none font-bold uppercase tracking-wider py-2 hover:bg-black hover:text-white">
                Add Lot
              </Button>
            </form>
          </div>

        </div>

        {/* Results Matrix */}
        <div className="lg:col-span-2 space-y-6 text-sm">
          
          <div className="grid grid-cols-3 gap-4 font-mono font-bold">
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Properties Simulated</span>
              <span className="text-3xl font-black font-display text-black dark:text-white">{properties.length}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Simulated Collection</span>
              <span className="text-3xl font-black font-display text-[#0044FF]">${Math.round(totalSharesCalculated).toLocaleString()}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Total Target Goal</span>
              <span className="text-3xl font-black font-display text-black dark:text-white">${calculatedCost.toLocaleString()}</span>
            </div>
          </div>

          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-xl uppercase tracking-tight">Assessment Calculations</h4>

            {properties.length === 0 ? (
              <p className="text-sm font-mono text-gray-400 py-8 border-2 border-dashed border-gray-300 text-center uppercase">Add lots to generate the assessment matrix.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F5F5F0] border-b-4 border-black font-mono font-bold uppercase tracking-wider text-black dark:bg-slate-900 dark:border-white dark:text-white">
                      <th className="py-2 px-3">Address</th>
                      <th className="py-2 px-3">Specs</th>
                      <th className="py-2 px-3">Formula Explanation</th>
                      <th className="py-2 px-3 text-right">Allocated Share</th>
                      <th className="py-2 px-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y-2 divide-gray-200 dark:divide-slate-800 font-bold">
                    {propertyShares.map(p => (
                      <tr key={p.id} className="hover:bg-[#FFD600]/10 transition-colors">
                        <td className="py-2 px-3 font-display uppercase tracking-tight text-sm text-black dark:text-white">{p.address}</td>
                        <td className="py-2 px-3 font-mono">
                          {p.lotWidth}x{p.lotDepth} ft ({p.area.toLocaleString()} sq ft)
                          {p.isCorner && <span className="bg-black text-[#FFD600] px-1 text-[8px] font-bold uppercase tracking-wider ml-1 border border-black font-mono">Corner</span>}
                        </td>
                        <td className="py-2 px-3 text-gray-500 font-mono text-[10px]">{p.explanation}</td>
                        <td className="py-2 px-3 text-right font-mono text-base text-[#0044FF]">${p.share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-2 px-3 text-center">
                          <button onClick={() => setProperties(prev => prev.filter(item => item.id !== p.id))} className="text-red-500 hover:text-red-700">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
}
